import './UIWallTypeCard.css'
import * as React from 'react';
import Paper from '@mui/material/Paper';
import { Divider, FormControlLabel, Grid, IconButton, InputAdornment, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {WallType, appdata} from '../internalModules';
import UIWallTypeGwp from './UIWallTypeGwp';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Slider from '@mui/material/Slider'
import { Box } from '@mui/system';
import { Data } from 'plotly.js';
import Plot from 'react-plotly.js';
import {WallTypeRibbon} from '../internalModules';
import { Dist } from '../Dist';
import UINameEdit from './UINameEdit';
import { mapSum } from '../utility';
import Graphics from '../Graphics';
import {AssemblyHorizontal} from '../internalModules';
import {ALayer, buckets } from '../internalModules';
import UILayerDialog from './UILayerDialog';
import UIAssemblyRow2 from './UIAssemblyRow2';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TrackAnalyticsEvent } from '../analytics/analytics';
import { DuplicateAssemblyEvent } from '../analytics/events';

type Props = {
  walltype: WallType
  deleteWallType: Function
  updateApp: Function
}

export default function UIWallTypeCard({walltype, deleteWallType, updateApp}: Props) {
  const [assembly, setAssembly] = React.useState(walltype)
  const [sliderValue, setSliderValue] = React.useState(assembly.wwr)
  const [exploded, setExploded] = React.useState(assembly.exploded)
  const [colorByCategory, setColorByCategory] = React.useState(assembly.colorByCategory)
  
  const elementRef = React.useRef<HTMLDivElement>(null)

  const getLayerCount = () => {
    let layerCount = assembly.layers.length
    if (assembly instanceof WallTypeRibbon) {
      layerCount += assembly.layersGlazed.length
    }
    if (assembly instanceof AssemblyHorizontal){
      layerCount += assembly.layers.length
      layerCount += assembly.layersFraming.length
    }
    return layerCount
  }

  const removeLayer = (bucket: string, index: number) => {
    assembly.removeLayer(bucket, index)
    updateApp()
  }

  const handleDuplicateWallType = () => {
    appdata.createDuplicateAssembly(assembly.guid)
    TrackAnalyticsEvent(DuplicateAssemblyEvent)
    updateApp()
    if (elementRef.current) {
      window.scrollBy({top: elementRef.current!.clientHeight, left: 0, behavior: 'smooth'})
    }
  }

  const handleDeleteWallType = () => {
    deleteWallType(assembly.guid)
    updateApp()
  }

  const handleSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
    setSliderValue(value as number)
  }

  const handleSliderCommitted = (event: React.SyntheticEvent | Event, value: number | Array<number>) =>{
    assembly.wwr = value as number
    setSliderValue(value as number)
    updateApp()
  }

  const handleChangeModule = (event: React.ChangeEvent<HTMLInputElement> ) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    let dist = Dist.feet(Number.parseFloat((event.target as HTMLInputElement).value))
    if (!(dist.value > 0)){
      dist = Dist.feet(0.5)
    }
    if (propertyName === 'mWidth') {
      assembly.updateWidth(dist)
      updateApp()
    }
    if (propertyName === 'mHeight') {
      assembly.updateHeight(dist)
      updateApp()
    }
    if (assembly instanceof AssemblyHorizontal && propertyName === 'mLength'){
      assembly.updateLength(dist)
      updateApp()
    }
  }

  const handleChangeExplode = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    setExploded(checked)
    assembly.exploded = checked
    updateApp()
  }

  const handleChangeColor = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    setColorByCategory(checked)
    assembly.colorByCategory = checked
    updateApp()
  }

  function handleDragEnd(e: DragEndEvent){
    const active = e.active
    const over = e.over!
    if (active.id !== over.id) {
      const layerBucket = appdata.getLayerBucketById(assembly, active.id.toString())
      let layers = assembly.getLayers(layerBucket)
      const oldIndex = layers.map(l=>(l.id)).indexOf(active.id.toString())
      const newIndex = layers.map(l=>(l.id)).indexOf(over.id.toString())
      if (oldIndex > -1 && newIndex > -1){
        assembly.reorderLayer(layerBucket, oldIndex, newIndex)
        updateApp()
      }
    }
  }

  const updateName = (name: string) => {
    assembly.name = name
    updateApp()
  }

  const [dialogWallOpen, setDialogWallOpen] = React.useState(false)
  const [dialogGlazedOpen, setDialogGlazedOpen] = React.useState(false)
  const [dialogFramingOpen, setDialogFramingOpen] = React.useState(false)
  const [dialogColumnOpen, setDialogColumnOpen] = React.useState(false)

  const toggleDialogWallOpen = (open: boolean) => (event: React.MouseEvent) => {
    setDialogWallOpen(open)
  }
  const toggleDialogGlazedOpen = (open: boolean) => (event: React.MouseEvent) => {
    setDialogGlazedOpen(open)
  }
  const toggleDialogFramingOpen = (open: boolean) => (event: React.MouseEvent) => {
    setDialogFramingOpen(open)
  }
  const toggleDialogColumnOpen = (open: boolean) => (event: React.MouseEvent) => {
    setDialogColumnOpen(open)
  }

  function acceptDialog(layer: ALayer, mode: string, layerBucket: string, layerIndex: number){
    // add layer to assembly
    if (mode === 'add'){
      assembly.addLayer(layerBucket, layer)
    }
    toggleDialogWallOpen(false)
    updateApp()
  }

  const makeWallControls = () => {
    if ( !(assembly instanceof AssemblyHorizontal) ){
      return(
        <>
          <Grid container alignItems={'center'}>
            <Grid item>
              <Typography variant='body1' fontWeight={'bold'}>Layers:</Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title='Add a layer'>
                <IconButton
                  sx={{borderRadius: 0}}
                  onClick={toggleDialogWallOpen(true)}
                >
                  <AddCircleOutlineIcon color={'secondary'}/>
                </IconButton>
              </Tooltip>
            </Grid>
            {/* {assembly.getLayers(buckets.wall).length < 1 ?
            <>
              <Grid item>
                <WestIcon color={'secondary'}/>
              </Grid>
              <Grid item>
                <Typography color={'secondary'}>Add a layer to the assembly</Typography>
              </Grid>
            </>
            : <></>} */}
          </Grid>
          <UILayerDialog key={assembly.guid + buckets.wall + '.add'} assemblyGuid={assembly.guid} bucket={buckets.wall}
          index={-1} isOpen={dialogWallOpen} 
          closeDialog={toggleDialogWallOpen(false)}
          acceptDialog={acceptDialog}/>
          <DndContext id={assembly.guid + buckets.wall} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={assembly.layers.map(l=>l.id)} strategy={verticalListSortingStrategy}>
            {
              assembly.layers.map( (item, index) => (
                <UIAssemblyRow2 key={item.id} assemblyGuid={assembly.guid}
                bucket={buckets.wall} index={index}
                removeAssemblyLayer={removeLayer}
                updateApp={updateApp}/>
              ))
            }
            </SortableContext>

          </DndContext>
          
        </>

      )
    }
    return (<></>)
  }

  const makeWindowControls = () => {
    if (assembly instanceof WallTypeRibbon) {
      return (
        <>
          <Grid container alignItems={'center'} marginTop={2}>
            <Grid item>
              <Typography variant='body1' fontWeight={'bold'}>Window Layers:</Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title='Add a layer'>
                <IconButton
                  sx={{borderRadius: 0}}
                  onClick={toggleDialogGlazedOpen(true)}
                >
                  <AddCircleOutlineIcon color={'secondary'}/>
                </IconButton>
              </Tooltip>
            </Grid>
            {/* {assembly.getLayers(buckets.glazed).length < 1 ?
            <>
              <Grid item>
                <WestIcon color={'secondary'}/>
              </Grid>
              <Grid item>
                <Typography color={'secondary'} >Add a window layer to the assembly</Typography>
              </Grid>
            </>
            : <></>} */}
          </Grid>
          <UILayerDialog key={assembly.guid + buckets.glazed + '.add'} assemblyGuid={assembly.guid} bucket={buckets.glazed}
          index={-1} isOpen={dialogGlazedOpen} 
          closeDialog={toggleDialogGlazedOpen(false)}
          acceptDialog={acceptDialog}/>
          <DndContext id={assembly.guid + buckets.glazed} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={assembly.layersGlazed.map(l=>l.id)} strategy={verticalListSortingStrategy}>
              {
                assembly.layersGlazed.map( (item, index) => (
                  <UIAssemblyRow2 key={item.id} assemblyGuid={assembly.guid}
                  bucket={buckets.glazed} index={index}
                  removeAssemblyLayer={removeLayer}
                  updateApp={updateApp}/>
                ))
              }
            </SortableContext>
          </DndContext>

          <Grid container marginTop={2}>
            <Grid item xs={3} >
              <Typography variant='body1' fontWeight={'bold'}>Window wall ratio:</Typography>
            </Grid>
            <Grid item xs={9} >
              <Slider
                key={assembly.guid}
                aria-label="Window to Wall Ratio"
                value={sliderValue}
                valueLabelDisplay="auto"
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderCommitted}
                step={0.01}
                //marks
                min={0.05}
                max={0.95}
                color='primary'
              />
            </Grid>
          </Grid>
        </>
      )
    }
    return (<></>)
  }

  const makeHorizontalAssemblyControls = () => {
    if (assembly instanceof AssemblyHorizontal){
      return(
        <>
          <Grid container alignItems={'center'}>
            <Grid item>
              <Typography variant='body1' fontWeight={'bold'}>Solid Layers:</Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title='Add a horizontal layer'>
                <IconButton
                  sx={{borderRadius: 0}}
                  onClick={toggleDialogWallOpen(true)}
                >
                  <AddCircleOutlineIcon color={'secondary'}/>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <UILayerDialog key={assembly.guid + buckets.hSolid + '.add'} assemblyGuid={assembly.guid} bucket={buckets.hSolid}
            index={-1} isOpen={dialogWallOpen} 
            closeDialog={toggleDialogWallOpen(false)}
            acceptDialog={acceptDialog}/>
          <DndContext id={assembly.guid + buckets.hSolid} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={assembly.layers.map(l=>l.id)} strategy={verticalListSortingStrategy}>
              {
                assembly.layers.map( (item, index) => (
                  <UIAssemblyRow2 key={item.id} assemblyGuid={assembly.guid}
                  bucket={buckets.hSolid} index={index}
                  removeAssemblyLayer={removeLayer}
                  updateApp={updateApp}/>
                )).reverse()
              }
            </SortableContext>
          </DndContext>
          
          <Grid container alignItems={'center'} marginTop={2}>
            <Grid item>
              <Typography variant='body1' fontWeight={'bold'}>Structural Framing:</Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title='Add a framing layer'>
                <IconButton sx={{borderRadius: 0}}
                  onClick={toggleDialogFramingOpen(true)}
                >
                  <AddCircleOutlineIcon color={'secondary'}/>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <UILayerDialog key={assembly.guid + buckets.hFraming + '.add'} assemblyGuid={assembly.guid} bucket={buckets.hFraming}
            index={-1} isOpen={dialogFramingOpen} 
            closeDialog={toggleDialogFramingOpen(false)}
            acceptDialog={acceptDialog}/>
          <DndContext id={assembly.guid + buckets.hFraming} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={assembly.layersFraming.map(l=>l.id)} strategy={verticalListSortingStrategy}>
              {
                assembly.layersFraming.map((item, index) => (
                  <UIAssemblyRow2 key={item.id} assemblyGuid={assembly.guid}
                  bucket={buckets.hFraming} index={index} 
                  removeAssemblyLayer={removeLayer}
                  updateApp={updateApp}/>
                )).reverse()
              }
            </SortableContext>
          </DndContext>

          <Grid container alignItems={'center'}>
            <Grid item>
              <Typography variant='body1' fontWeight={'bold'}>Column:</Typography>
            </Grid>
            <Grid item>
              <Tooltip arrow title='Add a column'>
                <IconButton sx={{borderRadius: 0}} onClick={toggleDialogColumnOpen(true)}>
                  <AddCircleOutlineIcon color={'secondary'}/>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <UILayerDialog key={assembly.guid + buckets.column + '.add'} assemblyGuid={assembly.guid} bucket={buckets.column}
            index={-1} isOpen={dialogColumnOpen} 
            closeDialog={toggleDialogColumnOpen(false)}
            acceptDialog={acceptDialog}/>
          {
            assembly.layersColumn.map((item, index) => (
              <UIAssemblyRow2 key={index} assemblyGuid={assembly.guid}
              bucket={buckets.column} index={index}
              removeAssemblyLayer={removeLayer}
              updateApp={updateApp}/>
            )).reverse()
          }
        </>
      )
    }
    else {
      return(<></>)
    }
  }

  const getPlotDataBar = () => {
    const sortedMap = new Map([...assembly.gwpsfByMaterial].sort( (a,b) => b[1] - a[1] ))
    const total = mapSum(sortedMap)
    let data = [] as Data[]
    //get data for materials
    for (const [materialName, gwpsf] of sortedMap) {
      const gwp = Number.parseFloat(gwpsf.toFixed(2))
      const intensity = gwp / total
      let wtData = {
        x: [gwp],
        y: [''],
        text: [materialName],
        name: materialName,
        orientation: 'h',
        type: 'bar',
        textposition: 'inside',
        hoverinfo: 'name+x',
        showlegend: false,
        marker: {
          line: {width: 3, color: '#ffffff'},
          color: Graphics.getColorStringByRange(intensity)
        },
      }
      data.push(wtData as Data)
    }
    
    return data
  }
  
  return (
    <Grid container p={3} ref={elementRef}>
      <Grid item sm={4} id={assembly.guid} className={'viewport'} >
        <Box position='absolute'>
          <UIWallTypeGwp gwpsf={assembly.gwpsf} horizontal={assembly instanceof AssemblyHorizontal}/>
        </Box>
      </Grid>

      <Grid item sm={7.75} xl={7}>
        <Paper sx={{p:2, minHeight:500, position:'relative'}}>
          <Grid container >
            <Grid item xs={10} display='flex' alignItems={'flex-center'}>
              <UINameEdit currentName={assembly.name} toolTip={'Rename assemby'} updateName={updateName}/>
            </Grid>
            <Grid item xs={2} display='flex' justifyContent={'right'}>
              <Tooltip arrow title='Duplicate assembly'>
                <IconButton sx={{borderRadius: 0}} onClick={handleDuplicateWallType}>
                  <ContentCopyIcon/>
                </IconButton>
              </Tooltip>
              <Tooltip arrow title='Delete assembly'>
                <IconButton sx={{borderRadius: 0} } onClick={handleDeleteWallType}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Divider/>
          <Grid container sx={{alignItems: 'center'}}>
            <Grid item xs={3} >
              <Typography variant='body1' fontWeight={'bold'}>Module dimensions:</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextField
                name='mWidth'
                variant='standard'
                size='small'
                value={assembly.width.ft}
                onChange={handleChangeModule}
                type='number'
                InputProps={{
                  startAdornment: <InputAdornment position="start">width(ft)</InputAdornment>,
                }}
                sx={{maxWidth: 150}}
              />
              {assembly instanceof AssemblyHorizontal?
                <TextField
                name='mLength'
                variant='standard'
                size='small'
                value={assembly.length.ft}
                onChange={handleChangeModule}
                type='number'
                InputProps={{
                  startAdornment: <InputAdornment position="start">length(ft)</InputAdornment>,
                }}
                sx={{maxWidth: 150}}
              />:
              <></>
              }
              <TextField
                name='mHeight'
                variant='standard'
                size='small'
                value={assembly.height.ft}
                onChange={handleChangeModule}
                type='number'
                InputProps={{
                  startAdornment: <InputAdornment position="start">height(ft)</InputAdornment>,
                }}
                sx={{maxWidth: 150}}
              />
              
            </Grid>
          </Grid>
          <Divider/>

          { getLayerCount() > 0 ?
            <Plot 
              className='barplot'
              data={getPlotDataBar()}
              layout={{
                barmode: 'stack',
                bargap: 0.05,
                autosize: true,
                title: '',
                paper_bgcolor: 'rgb(255, 255, 255)',
                plot_bgcolor: 'rgb(255, 255, 255)',
                //colorway: ['#005073', '#107dac', '#189ad3', '#71c7ec'],
                xaxis: {
                  autorange: true,
                  fixedrange: true,
                  showgrid: false,
                  zeroline: false,
                  showline: false,
                  autotick: true,
                  ticks: '',
                  showticklabels: false
                },
                yaxis: {
                  autorange: true,
                  fixedrange: true,
                  showgrid: false,
                  zeroline: false,
                  showline: false,
                  autotick: true,
                  ticks: '',
                  showticklabels: false
                },
                margin: { l: 0, r: 0, b: 5, t: 5, pad: 0}
              }}
              config={{responsive: true, displayModeBar: false}}
              style={{width: "100%", height: 50}}
              useResizeHandler={true}
            />
            : <></>
          }

          {makeWallControls()}

          {makeWindowControls()}

          {makeHorizontalAssemblyControls()}

          <Tooltip arrow title='Separate layers for clarity'>
            <FormControlLabel control={<Switch/>}
              value={assembly.exploded}
              label="Explode"
              onChange={handleChangeExplode}
            />
          </Tooltip>
          
          <Tooltip arrow title='Color geometry by category instead of impact'>
            <FormControlLabel control={<Switch/>}
              value={assembly.colorByCategory}
              label="Color by Category"
              onChange={handleChangeColor}
            />
          </Tooltip>

        </Paper>
      </Grid>
      
    </Grid>
  );
}
